<template>
  <div :class="$s.PrivacyPolicyPage">
    <div :class="$s.MainContainer">
      <section :class="$s.Header">
        <o-grid
          vertical-align="middle"
        >
          <o-grid-item
            :span="12"
            :tablet-landscape="6"
          >
            <o-heading
              :class="$s.MainHeaderText"
              size="display-1"
              element="h1"
            >
              {{ pageData.header.text }}
            </o-heading>
          </o-grid-item>

          <o-grid-item
            :span="12"
            :tablet-landscape="6"
          >
            <img
              :class="$s.HeaderImage"
              :alt="pageData.header.text"
              :src="headerImage"
            >
          </o-grid-item>
        </o-grid>
      </section>

      <o-grid
        :class="$s.PrivacyFormAndValueProp"
      >

        <o-grid-item
          :span="12"
          :tablet-landscape="5"
          :class="$s.PrivacyFormDescription"
        >
          <ul :class="$s.ValuePropList">
            <li :class="$s.ValuePropListItem">
              <o-icon
                :class="$s.ValuePropListIcon"
                name="check" />
              <span>{{ pageData.valueProps.valuePropOne }}</span>
            </li>
            <li :class="$s.ValuePropListItem">
              <o-icon
                :class="$s.ValuePropListIcon"
                name="check" />
              <span>{{ pageData.valueProps.valuePropTwo }}</span>
            </li>
            <li :class="$s.ValuePropListItem">
              <o-icon
                :class="$s.ValuePropListIcon"
                name="check" />
              <span>{{ pageData.valueProps.valuePropThree }}</span>
            </li>
          </ul>
        </o-grid-item>

        <o-grid-item
          :tablet-landscape="1"
          :class="$s.TabletLandscapeSpacer"
        />

        <o-grid-item
          :span="12"
          :tablet-landscape="6"
          :class="$s.FormContainer"
        >
          <terms-generator-form
            :page-data="pageData"
            :terms-properties="termsProperties"
            @formSubmit="handleFormSubmit"
          />
        </o-grid-item>
      </o-grid>
    </div>

    <div :class="$s.Disclaimer">
      <p>{{ pageData.disclaimer }}</p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import { OHeading } from 'orbit-ui/components/Heading';
import { OButton } from 'orbit-ui/components/Button';
import { OGrid, OGridItem } from 'orbit-ui/components/Grid';
import { OSelect } from 'orbit-ui/components/Select';
import { OIcon } from 'orbit-ui/components/Icon';
import { ModalLayer } from 'orbit-ui/components/Modal';
import { CLICK } from '@weebly/aragorn-analytics/dist/constants/actions';
import { LINK_BUTTON } from '@weebly/aragorn-analytics/dist/constants/categories';
import TermsAndConditionsImage from '~/assets/img/tools/terms-and-conditions-generator/terms-and-conditions-header.png';
import PrivacyPolicyImage from '~/assets/img/tools/privacy-policy/privacy-policy-header.png';

import TermsGeneratorModal from './TermsGeneratorSuccessModal';
import TermsGeneratorForm from './TermsGeneratorForm';

export default {
  name: 'TermsGeneratorPage',

  components: {
    OButton,
    OHeading,
    OGrid,
    OGridItem,
    OSelect,
    OIcon,
    TermsGeneratorForm,
  },

  inject: {
    modalLayer: ModalLayer,
  },

  props: {
    pageData: {
      type: Object,
      default: () => ({}),
    },
    termsProperties: {
      type: Object,
      default: () => ({}),
    },
  },

  computed: {
    headerImage() {
      let image = TermsAndConditionsImage;

      if (this.termsProperties.headerImage === 'privacy-policy') {
        image = PrivacyPolicyImage;
      }

      return `${this.cdnBaseUrl}${image}`;
    },

    ...mapGetters([
      'cdnBaseUrl',
    ]),
  },

  methods: {
    handleFormSubmit(formData) {
      const termsGeneratorUrlPath = this.generatePrivacyUrlPath(formData);

      this.modalLayer.open(TermsGeneratorModal, {
        props: {
          generatedTermsUrl: termsGeneratorUrlPath,
          pageData: this.pageData,
          termsProperties: this.termsProperties,
        },
      });

      this.$analytics.track({
        type: 'standard',
        location: this.termsProperties.trackingLocation,
        object_category: LINK_BUTTON,
        action: CLICK,
        object_instance: this.termsProperties.createTermsClickEventName,
      });
    },

    generatePrivacyUrlPath(formData) {
      const base64Data = this.convertToBase64(formData);
      return `${this.termsProperties.termsRetrievalPage}/${base64Data}`;
    },

    generatePrivacyAbsoluteUrl(urlPath) {
      return `${window.location.origin}${urlPath}`;
    },

    convertToBase64(formData) {
      const formDataString = JSON.stringify(formData);
      // The encodeURI and unescape parts are there to deal with unicode characters.
      // This code was taken from:
      // https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/btoa#Unicode_Strings
      return window.btoa(unescape(encodeURIComponent(formDataString)));
    },
  },
};
</script>

<style module="$s" lang="scss">
.PrivacyPolicyPage {
  max-width: 1176px;
  margin: 0 auto;
}

.MainContainer {
  padding: 20px;

  @media (--for-tablet-landscape-up) {
    background-color: var(--color-gray-10);
    padding: 10px 40px 35px;
  }
}

.MainHeaderText {
  padding: 0 15px;

  @media (--for-tablet-landscape-up) {
    margin-top: 50px;
    padding: unset;
  }
}

.Header {
  background-color: var(--color-gray-10);
  margin: -20px -20px 25px;
  padding: 20px;

  @media (--for-tablet-landscape-up) {
    background-color: unset;
    margin: unset;
    padding: unset;
  }
}

.HeaderImage {
  max-width: 100%;
  margin: -30px auto -45px;

  @media (--for-tablet-landscape-up) {
    margin: 0 auto -25px;
  }
}

.TabletLandscapeSpacer {
  display: none;

  @media (--for-tablet-landscape-up) {
    display: block;
  }
}

.ValuePropList {
  list-style-type: none;
  padding-left: 0;
  margin: 10px 0 -30px 40px;

  @media (--for-tablet-landscape-up) {
    margin: 58px 0 0 80px;
  }

  .ValuePropListItem {
    position: relative;
    margin-bottom: var(--space-x3);
    padding-left: 10px;

    span {
      font-size: 18px;
      color: var(--color-gray-60);
      line-height: 32px;
    }

    .ValuePropListIcon {
      left: -40px;
      top: 5px;
      position: absolute;
      line-height: inherit;
      color: var(--brand-color);
      padding: 6px;
      border-radius: 50px;
      border: 2px solid var(--brand-color);
    }
  }
}

.FormContainer {
  padding-right: 16px;
}

.Disclaimer {
  padding: 20px;
  font-size: 13px;
  color: var(--color-gray-50);

  @media (--for-tablet-landscape-up) {
    padding: 40px;
  }
}
</style>
